/* EXC: TikTok video sections (exc-ugc-reels on the homepage, exc-ugc-pdp on the
   product page) and the full-screen player they share.

   Speed rules:
   - No video file loads with the page. A homepage card gets its file only when
     it is about to scroll into view, plays muted while at least 60% of it is
     on screen and pauses the moment it is not. Product page cards never load a
     video until tapped.
   - Riders with reduced motion or data saver on get still cards with a play
     button instead of autoplay.
   Notes: hq/projects/excdesigns/theme-build-notes.md (2026-10-20) */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const saveData = Boolean(navigator.connection && navigator.connection.saveData);

// Shopify often keeps one 1080p MP4 per video. Browsers that play the adaptive
// stream (Safari on every iPhone, newer Chrome) get it instead and drop to a
// lighter size on a slow connection; everyone else gets the MP4.
const canHls = document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';
const pickSrc = (el) => (canHls && el.dataset.hls ? el.dataset.hls : el.dataset.src);

// Cards are under 300px wide, so they play the stream's smallest level (480p,
// about 0.9 Mbps) instead of letting a fast connection pull 1080p for a
// thumbnail. Measured 2026-10-20: two cards on the adaptive stream took 8.6 MB.
const masters = new Map();
const cardSrc = (video) => {
  const hls = video.dataset.hls;
  if (!canHls || !hls) return Promise.resolve(video.dataset.src);
  if (!masters.has(hls)) {
    const base = new URL(hls, location.href);
    masters.set(
      hls,
      fetch(base)
        .then((r) => r.text())
        .then((text) => {
          const lines = text.split(/\r?\n/);
          let best = null;
          lines.forEach((line, i) => {
            const bw = /BANDWIDTH=(\d+)/.exec(line);
            if (!line.startsWith('#EXT-X-STREAM-INF') || !bw || !lines[i + 1]) return;
            if (!best || Number(bw[1]) < best.bw) best = { bw: Number(bw[1]), uri: lines[i + 1].trim() };
          });
          return best ? new URL(best.uri, base).href : base.href;
        })
        .catch(() => video.dataset.src)
    );
  }
  return masters.get(hls);
};

const ensureSrc = (video) => {
  if (!video.srcPromise) {
    video.srcPromise = cardSrc(video).then((src) => {
      video.src = src;
      return src;
    });
  }
  return video.srcPromise;
};

class ExcUgc extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.track = this.querySelector('[data-ugc-track]');
    this.openers = [...this.querySelectorAll('[data-ugc-open]')];
    this.viewer = this.querySelector('[data-ugc-viewer]');
    this.index = 0;
    this.visible = new Set();

    this.openers.forEach((btn, i) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(i, btn);
      });
    });

    if (this.hasAttribute('data-autoplay') && !reduceMotion && !saveData) this.setupAutoplay();
    this.setupArrows();
    if (this.viewer) this.setupViewer();
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    if (this.ro) this.ro.disconnect();
    this.close();
  }

  /* ---------- homepage cards: muted autoplay while on screen ---------- */
  setupAutoplay() {
    const videos = [...this.querySelectorAll('video[data-src]')];
    if (!videos.length || !('IntersectionObserver' in window)) return;

    videos.forEach((video) => {
      video.muted = true;
      video.addEventListener('playing', () => {
        video.classList.add('is-playing');
        video.closest('[data-ugc-open]')?.classList.add('is-live');
      });
    });

    // Point the card at its file a little before it arrives, so it starts fast.
    this.loader = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const video = entry.target;
          // Only the first few seconds: a card that never plays never downloads
          // the whole clip.
          video.preload = 'metadata';
          ensureSrc(video);
          this.loader.unobserve(video);
        });
      },
      { rootMargin: '200px 200px' }
    );

    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.visible.add(video);
            ensureSrc(video).then(() => {
              if (!this.viewerOpen && this.visible.has(video)) video.play().catch(() => {});
            });
          } else {
            this.visible.delete(video);
            video.pause();
          }
        });
      },
      { threshold: [0, 0.6] }
    );

    videos.forEach((video) => {
      this.loader.observe(video);
      this.io.observe(video);
    });
  }

  /* ---------- desktop arrows ---------- */
  setupArrows() {
    this.prevBtn = this.querySelector('[data-ugc-prev]');
    this.nextBtn = this.querySelector('[data-ugc-next]');
    if (!this.track) return;

    const update = () => {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.classList.toggle('is-scrollable', max > 2);
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= max - 2;
      const card = this.track.querySelector('[data-ugc-open]');
      if (card) this.style.setProperty('--exc-ugc-card-h', `${card.offsetHeight}px`);
    };

    const step = (dir) => {
      const item = this.track.querySelector('.exc-ugc__item');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const per = item ? item.offsetWidth + gap : this.track.clientWidth;
      const count = Math.max(1, Math.floor(this.track.clientWidth / per));
      this.track.scrollBy({ left: dir * per * count, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    this.prevBtn?.addEventListener('click', () => step(-1));
    this.nextBtn?.addEventListener('click', () => step(1));
    this.track.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(update);
      this.ro.observe(this.track);
    }
    update();
  }

  /* ---------- full-screen player ---------- */
  setupViewer() {
    const q = (sel) => this.viewer.querySelector(sel);
    this.v = {
      video: q('[data-ugc-v-video]'),
      paused: q('[data-ugc-v-paused]'),
      bar: q('[data-ugc-v-bar]'),
      count: q('[data-ugc-v-count]'),
      mute: q('[data-ugc-v-mute]'),
      close: q('[data-ugc-v-close]'),
      prev: q('[data-ugc-v-prev]'),
      next: q('[data-ugc-v-next]'),
      creator: q('[data-ugc-v-creator]'),
      caption: q('[data-ugc-v-caption]'),
      tiktok: q('[data-ugc-v-tiktok]'),
      part: q('[data-ugc-v-part]'),
      partImg: q('[data-ugc-v-part-img]'),
      partTitle: q('[data-ugc-v-part-title]'),
      partPrice: q('[data-ugc-v-part-price]'),
    };
    const { video } = this.v;

    this.v.close.addEventListener('click', () => this.close());
    this.v.prev.addEventListener('click', () => this.go(-1));
    this.v.next.addEventListener('click', () => this.go(1));
    this.v.mute.addEventListener('click', () => {
      video.muted = !video.muted;
      this.syncMute();
    });

    video.addEventListener('click', () => {
      if (video.paused) video.play().catch(() => {});
      else video.pause();
    });
    video.addEventListener('play', () => (this.v.paused.hidden = true));
    video.addEventListener('pause', () => {
      if (this.viewerOpen) this.v.paused.hidden = false;
    });
    video.addEventListener('timeupdate', () => {
      const p = video.duration ? video.currentTime / video.duration : 0;
      this.v.bar.style.transform = `scaleX(${p})`;
    });

    this.viewer.addEventListener('close', () => this.afterClose());
    this.viewer.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.go(1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.go(-1);
      }
    });
    // Tap on the dimmed area around the desktop player closes it.
    this.viewer.addEventListener('click', (event) => {
      if (event.target === this.viewer) this.close();
    });

    // Swipe: left or up for the next video, right or down for the one before.
    let sx = 0;
    let sy = 0;
    this.viewer.addEventListener(
      'touchstart',
      (event) => {
        sx = event.touches[0].clientX;
        sy = event.touches[0].clientY;
      },
      { passive: true }
    );
    this.viewer.addEventListener(
      'touchend',
      (event) => {
        const dx = event.changedTouches[0].clientX - sx;
        const dy = event.changedTouches[0].clientY - sy;
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 50) return;
        if (Math.abs(dx) > Math.abs(dy)) this.go(dx < 0 ? 1 : -1);
        else this.go(dy < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  open(i, opener) {
    if (!this.viewer || !this.openers.length) return;
    this.opener = opener;
    this.viewerOpen = true;
    this.visible.forEach((v) => v.pause());
    document.documentElement.classList.add('exc-ugc-lock');
    if (!this.viewer.open) this.viewer.showModal();
    this.v.video.muted = false;
    this.syncMute();
    this.show(i);
  }

  go(dir) {
    const next = this.index + dir;
    if (next < 0 || next >= this.openers.length) return;
    this.show(next);
  }

  show(i) {
    this.index = i;
    const d = this.openers[i].dataset;
    const { video } = this.v;
    const total = this.openers.length;

    video.poster = d.poster || '';
    video.src = pickSrc(this.openers[i]);
    video.currentTime = 0;
    video.play().catch(() => {
      // A browser that refuses sound without a fresh tap: play muted instead.
      video.muted = true;
      this.syncMute();
      video.play().catch(() => (this.v.paused.hidden = false));
    });

    this.v.count.textContent = total > 1 ? `${i + 1} / ${total}` : '';
    this.v.prev.disabled = i === 0;
    this.v.next.disabled = i === total - 1;
    this.v.creator.textContent = d.creator || '';
    this.v.caption.textContent = d.caption || '';

    if (d.tiktok) {
      this.v.tiktok.href = d.tiktok;
      this.v.tiktok.textContent = d.platform === 'instagram' ? 'Watch on Instagram' : 'Watch on TikTok';
      this.v.tiktok.hidden = false;
    } else {
      this.v.tiktok.hidden = true;
    }

    if (d.partUrl) {
      this.v.part.href = d.partUrl;
      this.v.partTitle.textContent = d.partTitle || '';
      this.v.partPrice.textContent = d.partPrice || '';
      if (d.partImg) {
        this.v.partImg.src = d.partImg;
        this.v.partImg.hidden = false;
      } else {
        this.v.partImg.hidden = true;
      }
      this.v.part.hidden = false;
    } else {
      this.v.part.hidden = true;
    }
  }

  syncMute() {
    const muted = this.v.video.muted;
    this.v.mute.setAttribute('aria-pressed', String(muted));
    this.v.mute.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');
  }

  close() {
    if (this.viewer && this.viewer.open) this.viewer.close();
  }

  afterClose() {
    this.viewerOpen = false;
    const { video } = this.v;
    video.pause();
    video.removeAttribute('src');
    video.load();
    this.v.paused.hidden = true;
    document.documentElement.classList.remove('exc-ugc-lock');
    this.visible.forEach((v) => v.play().catch(() => {}));
    if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
  }
}

if (!customElements.get('exc-ugc')) customElements.define('exc-ugc', ExcUgc);
